import React, { useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

// framer-motion (12.11) laat een element bij een hardware-versnelde (WAAPI)
// opacity-fade 1 frame op de beginwaarde staan zodra de fade klaar is
// (flikkering). Met een onUpdate prop valt framer terug op de JS-animatie
// (zie ook noWaapi in App.js).
const noWaapi = () => {};

const OrderItem = ({ customer, timestamp, portal, fadeKey, isSleeping }) => {
  const textColor = isSleeping ? "rgba(255,255,255,0.9)" : "#111";
  const timeColor = isSleeping ? "rgba(255,255,255,0.7)" : "#111";

  const getTimeElapsed = (orderTime) => {
    const now = Date.now();
    const orderDate = new Date(orderTime);
    const diffInMinutes = Math.floor((now - orderDate) / (1000 * 60));
    if (diffInMinutes === 0) {
      return "Just now";
    } else if (diffInMinutes < 0) {
      return "88 mph";
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
    } else if (diffInMinutes < 24 * 60) {
      const diffInHours = Math.floor(diffInMinutes / 60);
      return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
    } else {
      const diffInDays = Math.floor(diffInMinutes / (60 * 24));
      return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
    }
  };

  const timeText = useMemo(
    () => getTimeElapsed(timestamp),
    [timestamp, fadeKey]
  );
  const customerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const customerEl = customerRef.current;
    const scrollContainerEl = scrollContainerRef.current;
    if (!customerEl || !scrollContainerEl) return;

    const checkOverflow = () => {
      customerEl.style.animation = "none";
      void customerEl.offsetWidth; // force reflow
      const scrollWidth = customerEl.scrollWidth;
      const clientWidth = scrollContainerEl.clientWidth;
      if (scrollWidth > clientWidth) {
        customerEl.style.setProperty("--container-width", `${clientWidth}px`);
        customerEl.style.animation = "scrollText 8s ease-in-out infinite alternate";
      }
    };

    const rafId = requestAnimationFrame(checkOverflow);

    const ro = new ResizeObserver(checkOverflow);
    ro.observe(scrollContainerEl);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [customer]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 0",
        height: "58px",
        letterSpacing: "-0.02em",
      }}
    >
      <div
        ref={scrollContainerRef}
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <div
          ref={customerRef}
          style={{
            color: textColor,
            fontFamily: '"DM Sans"',
            fontSize: "20px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "28px",
            display: "inline-block",
            whiteSpace: "nowrap",
            transition: "color 0.3s ease",
            transform: "translateZ(0)",
          }}
        >
          {customer}
        </div>
      </div>
      <div
        style={{
          color: textColor,
          fontFamily: '"DM Sans"',
          fontSize: "20px",
          fontStyle: "normal",
          fontWeight: 500,
          lineHeight: "28px",
          textAlign: "right",
          transition: "color 0.3s ease",
          whiteSpace: "nowrap",
          flexShrink: 0,
          paddingLeft: "8px",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.span
            key={timeText}
            initial={{ opacity: 0 }}
            animate={{ opacity: isSleeping ? 0.4 : 0.4 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            onUpdate={noWaapi}
            style={{ color: timeColor }}
          >
            {timeText}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
};

const ReceivedComponent = ({ className, orders, isSleeping = false }) => {
  const containerRef = useRef(null);
  const [hasMounted, setHasMounted] = React.useState(false);
  const role = localStorage.getItem("role");

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const fadeKey = useMemo(() => Date.now(), [orders]);

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      @keyframes scrollText {
        0% { transform: translateX(0); }
       100% { transform: translateX(calc(-100% + var(--container-width))); }
      }
    `;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  const headerColor = isSleeping ? "rgba(255,255,255,0.7)" : "#111";

  return (
    <div
      className={className}
      style={{
        maxWidth: "411px",
        width: "84%",
        height: "148px",
        padding: "0 16px 16px 16px",
        backgroundColor: "rgba(255, 255, 255, 0.00)",
        pointerEvents: "none",
      }}
    >
      <h2
        style={{
          color: headerColor,
          fontFamily: '"DM Sans"',
          fontSize: "14px",
          fontStyle: "normal",
          fontWeight: 500,
          lineHeight: "16px",
          zIndex: 5,
          opacity: 0.4,
          marginBottom: "0",
          transition: "color 0.3s ease",
        }}
      >
        Last Received
      </h2>
      <div
        ref={containerRef}
        style={{
          position: "relative",
          height: "116px",
          overflow: "hidden",
        }}
      >
        {!hasMounted &&
          orders.slice(0, 2).map((order, index) => (
            <div
              key={`static-${order.stamp}-${order.customer}-${index}`}
              style={{
                position: "absolute",
                width: "100%",
                top: `${index * 58}px`,
              }}
            >
              <OrderItem
                customer={order.customer}
                timestamp={order.stamp}
                fadeKey={`${order.stamp}-${order.customer}`}
                isSleeping={isSleeping}
              />
            </div>
          ))}
        <AnimatePresence>
          {hasMounted &&
            orders.slice(0, 2).map((order, index) => (
              <motion.div
                key={`${order.stamp}-${order.customer}-${index}`}
                initial={{ opacity: 0, y: -58 }}
                animate={{ opacity: 1, y: index * 58 }}
                exit={{ opacity: 0, y: 58 }}
                transition={{ duration: 0.5 }}
                onUpdate={noWaapi}
                style={{
                  position: "absolute",
                  width: "100%",
                  top: 0,
                }}
              >
                <OrderItem
                  customer={role === "Demo" ? order.portal : order.customer}
                  timestamp={order.stamp}
                  fadeKey={fadeKey}
                  isSleeping={isSleeping}
                />
              </motion.div>
            ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ReceivedComponent;